import React, { useState, useMemo } from "react";
import { SectionCard, Input, Textarea, inputCls } from "./ui";
import { useProject } from "@/context/ProjectContext";
import { uid } from "@/lib/uid";
import { downloadGanttPdf } from "@/lib/pdf";
import { toast } from "sonner";
import SupportingMaterials from "./SupportingMaterials";
import { Plus, Trash2, GanttChartSquare, Download, X, Loader2 } from "lucide-react";

const STATUS = ["Planned", "In Progress", "Blocked", "Done"];
const STATUS_COLOR = { Planned: "bg-slate-400", "In Progress": "bg-amber-500", Blocked: "bg-red-500", Done: "bg-emerald-500" };
const STATUS_HEX = { Planned: "#94a3b8", "In Progress": "#f59e0b", Blocked: "#ef4444", Done: "#10b981" };
const DAY_MS = 86400000;

function GanttModal({ tasks, projectName, brandColor = "#10B981", onClose }) {
  const dated = tasks.filter((t) => t.start && t.end);
  const [downloading, setDownloading] = useState(false);
  const handleDownload = async () => {
    setDownloading(true);
    try { await downloadGanttPdf({ projectName, tasks: dated, brandColor, filename: `${projectName}-gantt` }); toast.success("Gantt PDF downloaded"); }
    catch (e) { toast.error("Gantt PDF download failed"); }
    finally { setDownloading(false); }
  };
  const { min, span, ticks } = useMemo(() => {
    if (!dated.length) return { min: null, span: 1, ticks: [] };
    const starts = dated.map((t) => new Date(t.start).getTime());
    const ends = dated.map((t) => new Date(t.end).getTime());
    const mn = Math.min(...starts);
    const mx = Math.max(...ends);
    const sp = Math.max(DAY_MS, mx - mn);
    const tk = [];
    const count = 5;
    for (let i = 0; i <= count; i++) {
      tk.push(new Date(mn + (sp * i) / count));
    }
    return { min: mn, span: sp, ticks: tk };
  }, [dated]);

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/85 backdrop-blur-sm flex flex-col" data-testid="gantt-modal">
      <div className="no-print h-16 flex items-center justify-between px-6 bg-slate-900 text-white border-b border-slate-800 flex-shrink-0">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-emerald-400 font-semibold">Implementation Gantt</p>
          <h2 className="font-display font-semibold truncate">{projectName}</h2>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="gantt-print-btn" onClick={handleDownload} disabled={downloading} className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-emerald-500 hover:bg-emerald-600 transition-colors disabled:opacity-60">
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download PDF
          </button>
          <button data-testid="gantt-close-btn" aria-label="Close" onClick={onClose} className="h-9 w-9 flex items-center justify-center rounded-md hover:bg-slate-800 transition-colors"><X className="h-5 w-5" /></button>
        </div>
      </div>

      <div className="flex-1 overflow-auto py-8 px-4 flex justify-center">
        <div id="print-area" className="doc-page shadow-2xl" style={{ padding: "0.6in" }}>
          <h1 className="text-xl font-bold text-slate-900 mb-1">Implementation Timeline — Gantt</h1>
          <p className="text-xs text-slate-500 mb-5">{projectName}</p>
          {dated.length === 0 ? (
            <p className="text-sm text-slate-400">Add start and end dates to tasks to render the Gantt chart.</p>
          ) : (
            <div>
              <div className="flex text-[10px] text-slate-400 mb-1 ml-44">
                {ticks.map((d) => (
                  <div key={d.getTime()} className="flex-1 text-left">{d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}</div>
                ))}
              </div>
              <div className="space-y-2">
                {dated.map((t, i) => {
                  const s = new Date(t.start).getTime();
                  const e = new Date(t.end).getTime();
                  const left = ((s - min) / span) * 100;
                  const width = Math.max(2, ((e - s) / span) * 100);
                  return (
                    <div key={t.id || i} className="flex items-center gap-2" data-testid={`gantt-bar-${i}`}>
                      <span className="w-44 truncate text-xs text-slate-700 flex-shrink-0">{t.name || "Untitled"}</span>
                      <div className="flex-1 h-5 bg-slate-100 rounded relative">
                        <div className="absolute h-5 rounded flex items-center px-2" style={{ left: `${left}%`, width: `${width}%`, background: STATUS_HEX[t.status] || "#94a3b8" }}>
                          <span className="text-[9px] text-white font-medium truncate">{t.status}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-5 flex gap-4 text-[10px] text-slate-500">
                {STATUS.map((s) => (
                  <span key={s} className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: STATUS_HEX[s] }} /> {s}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Phase4() {
  const { phaseData: pd, setPhase, project } = useProject();
  const p = pd.phase4 || { tasks: [] };
  const tasks = p.tasks || [];
  const [ganttOpen, setGanttOpen] = useState(false);
  const setTasks = (t) => setPhase("phase4", { ...p, tasks: t });
  const add = () => setTasks([...tasks, { id: uid(), name: "", owner: "", start: "", end: "", status: "Planned" }]);
  const upd = (i, k, v) => { const t = [...tasks]; t[i] = { ...t[i], [k]: v }; setTasks(t); };
  const del = (i) => setTasks(tasks.filter((_, idx) => idx !== i));

  const deliverables = p.deliverables || [];
  const setDel = (d) => setPhase("phase4", { ...p, deliverables: d });
  const addDeliv = () => setDel([...deliverables, { id: uid(), name: "", details: "", due: "" }]);
  const updDeliv = (i, k, v) => { const d = [...deliverables]; d[i] = { ...d[i], [k]: v }; setDel(d); };
  const delDeliv = (i) => setDel(deliverables.filter((_, idx) => idx !== i));

  const reviewCycle = p.review_cycle || { plan: [] };
  const rcPlan = reviewCycle.plan || [];
  const setRc = (next) => setPhase("phase4", { ...p, review_cycle: next });
  const addRc = () => setRc({ ...reviewCycle, plan: [...rcPlan, { id: uid(), milestone: "", who: "", what: "" }] });
  const updRc = (i, k, v) => { const a = [...rcPlan]; a[i] = { ...a[i], [k]: v }; setRc({ ...reviewCycle, plan: a }); };
  const delRc = (i) => setRc({ ...reviewCycle, plan: rcPlan.filter((_, idx) => idx !== i) });

  const rollout = p.rollout_plan || [];
  const setRollout = (r) => setPhase("phase4", { ...p, rollout_plan: r });
  const addRollout = () => setRollout([...rollout, { id: uid(), phase: "", activities: "", owner: "", timeline: "" }]);
  const updRollout = (i, k, v) => { const a = [...rollout]; a[i] = { ...a[i], [k]: v }; setRollout(a); };
  const delRollout = (i) => setRollout(rollout.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-6 max-w-5xl">
      <SectionCard
        title="Implementation Timeline"
        desc="Track the rollout of selected interventions."
        right={
          <div className="flex items-center gap-2">
            <button data-testid="open-gantt-btn" onClick={() => setGanttOpen(true)} className="inline-flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"><GanttChartSquare className="h-4 w-4" /> Gantt View</button>
            <button data-testid="add-task-btn" onClick={add} className="inline-flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800"><Plus className="h-4 w-4" /> Add Task</button>
          </div>
        }
        testid="phase4-timeline"
      >
        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Task</th>
                <th className="text-left px-3 py-2 font-semibold">Owner</th>
                <th className="text-left px-3 py-2 font-semibold">Start</th>
                <th className="text-left px-3 py-2 font-semibold">End</th>
                <th className="text-left px-3 py-2 font-semibold">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {tasks.length === 0 && <tr><td colSpan={6} className="px-3 py-4 text-slate-400">No tasks yet. Add the first rollout milestone.</td></tr>}
              {tasks.map((t, i) => (
                <tr key={t.id || i} className="border-t border-slate-100" data-testid={`task-row-${i}`}>
                  <td className="px-3 py-2"><input value={t.name} onChange={(e) => upd(i, "name", e.target.value)} placeholder="Milestone" className="w-full outline-none bg-transparent" data-testid={`task-name-${i}`} /></td>
                  <td className="px-3 py-2"><input value={t.owner} onChange={(e) => upd(i, "owner", e.target.value)} placeholder="Owner" className="w-28 outline-none bg-transparent" /></td>
                  <td className="px-3 py-2"><input type="date" value={t.start} onChange={(e) => upd(i, "start", e.target.value)} className="outline-none bg-transparent text-slate-600" /></td>
                  <td className="px-3 py-2"><input type="date" value={t.end} onChange={(e) => upd(i, "end", e.target.value)} className="outline-none bg-transparent text-slate-600" /></td>
                  <td className="px-3 py-2">
                    <select value={t.status} onChange={(e) => upd(i, "status", e.target.value)} className="bg-transparent outline-none text-sm" data-testid={`task-status-${i}`}>
                      {STATUS.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2"><button onClick={() => del(i)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {tasks.length > 0 && (
          <div className="mt-6">
            <p className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-400 mb-3">Quick Timeline</p>
            <div className="space-y-2">
              {tasks.map((t, i) => (
                <div key={t.id || i} className="flex items-center gap-3">
                  <span className="w-40 truncate text-sm text-slate-700">{t.name || "Untitled"}</span>
                  <div className="flex-1 h-6 bg-slate-100 rounded-md relative overflow-hidden">
                    <div className={`h-full rounded-md ${STATUS_COLOR[t.status]} opacity-80 flex items-center px-2`} style={{ width: t.status === "Done" ? "100%" : t.status === "In Progress" ? "55%" : t.status === "Blocked" ? "35%" : "20%" }}>
                      <span className="text-[10px] text-white font-medium">{t.status}</span>
                    </div>
                  </div>
                  <span className="text-xs text-slate-400 w-44 text-right">{t.start || "—"} → {t.end || "—"}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </SectionCard>

      <SectionCard
        title="Project Scope — Deliverables"
        desc="Concrete deliverables the engagement will produce, plus what is explicitly out of scope."
        right={<button data-testid="add-deliverable-btn" onClick={addDeliv} className="inline-flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800"><Plus className="h-4 w-4" /> Add</button>}
        testid="phase4-scope"
      >
        <div className="space-y-2">
          {deliverables.length === 0 && <p className="text-sm text-slate-400">No deliverables yet.</p>}
          {deliverables.map((d, i) => (
            <div key={d.id || i} className="grid sm:grid-cols-[1fr_1.6fr_140px_auto] gap-2 items-center" data-testid={`deliverable-row-${i}`}>
              <Input value={d.name} onChange={(e) => updDeliv(i, "name", e.target.value)} placeholder="Deliverable" data-testid={`deliverable-name-${i}`} />
              <Input value={d.details} onChange={(e) => updDeliv(i, "details", e.target.value)} placeholder="Details" />
              <Input value={d.due} onChange={(e) => updDeliv(i, "due", e.target.value)} placeholder="Due (e.g. Wk 4)" />
              <button onClick={() => delDeliv(i)} className="text-slate-300 hover:text-red-500 justify-self-center"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-slate-700 mb-1">Out of Scope</label>
          <Textarea data-testid="out-of-scope-input" rows={2} value={p.out_of_scope || ""} onChange={(e) => setPhase("phase4", { ...p, out_of_scope: e.target.value })} placeholder="What this engagement will NOT cover…" />
        </div>
      </SectionCard>

      <SectionCard
        title="Review & Iteration Cycle"
        desc="Stakeholder review milestones, iteration cadence and version control (Design Document)."
        right={<button data-testid="add-review-milestone-btn" onClick={addRc} className="inline-flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800"><Plus className="h-4 w-4" /> Add</button>}
        testid="phase4-review-cycle"
      >
        <div className="space-y-2">
          {rcPlan.length === 0 && <p className="text-sm text-slate-400">No review milestones yet.</p>}
          {rcPlan.map((r, i) => (
            <div key={r.id || i} className="grid sm:grid-cols-[1fr_1fr_1.4fr_auto] gap-2 items-center" data-testid={`review-milestone-row-${i}`}>
              <Input value={r.milestone} onChange={(e) => updRc(i, "milestone", e.target.value)} placeholder="Review milestone" data-testid={`review-milestone-${i}`} />
              <Input value={r.who} onChange={(e) => updRc(i, "who", e.target.value)} placeholder="Who reviews" />
              <Input value={r.what} onChange={(e) => updRc(i, "what", e.target.value)} placeholder="What's being reviewed" />
              <button onClick={() => delRc(i)} className="text-slate-300 hover:text-red-500 justify-self-center"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Iteration & Approval Cycle</label>
            <Textarea data-testid="iteration-approval-input" rows={2} value={reviewCycle.iteration_approval || ""} onChange={(e) => setRc({ ...reviewCycle, iteration_approval: e.target.value })} placeholder="How feedback is incorporated and approved…" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Version Control</label>
            <Textarea data-testid="version-control-input" rows={2} value={reviewCycle.version_control || ""} onChange={(e) => setRc({ ...reviewCycle, version_control: e.target.value })} placeholder="Versioning approach…" />
          </div>
        </div>
      </SectionCard>

      <SectionCard
        title="Rollout Plan"
        desc="Phased rollout activities, owners and timeline, with a contingency plan (Design Document)."
        right={<button data-testid="add-rollout-btn" onClick={addRollout} className="inline-flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800"><Plus className="h-4 w-4" /> Add</button>}
        testid="phase4-rollout"
      >
        <div className="space-y-2">
          {rollout.length === 0 && <p className="text-sm text-slate-400">No rollout phases yet.</p>}
          {rollout.map((r, i) => (
            <div key={r.id || i} className="grid sm:grid-cols-[1fr_1.6fr_1fr_1fr_auto] gap-2 items-center" data-testid={`rollout-row-${i}`}>
              <Input value={r.phase} onChange={(e) => updRollout(i, "phase", e.target.value)} placeholder="Phase" data-testid={`rollout-phase-${i}`} />
              <Input value={r.activities} onChange={(e) => updRollout(i, "activities", e.target.value)} placeholder="Key activities" />
              <Input value={r.owner} onChange={(e) => updRollout(i, "owner", e.target.value)} placeholder="Owner(s)" />
              <Input value={r.timeline} onChange={(e) => updRollout(i, "timeline", e.target.value)} placeholder="Timeline" />
              <button onClick={() => delRollout(i)} className="text-slate-300 hover:text-red-500 justify-self-center"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-slate-700 mb-1">Contingency Plan</label>
          <Textarea data-testid="contingency-plan-input" rows={2} value={p.contingency_plan || ""} onChange={(e) => setPhase("phase4", { ...p, contingency_plan: e.target.value })} placeholder="Backup plans for delivery risks…" />
        </div>
      </SectionCard>

      <SupportingMaterials phaseKey="phase4" />

      {ganttOpen && <GanttModal tasks={tasks} projectName={project?.name || "Project"} brandColor={project?.branding?.brand_color || "#10B981"} onClose={() => setGanttOpen(false)} />}
    </div>
  );
}
